import { useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useSnapshot } from 'valtio';

import isLegacyOrganizationPlan from '@cartwhl/common/utils/isLegacyOrganizationPlan';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import Button from '@cartwhl/ui/input/Button';

import {
  authStore,
  customerStore,
  noticesStore,
  userStore,
} from 'stores/global';

import MyNewTeam from './MyNewTeam';
import MyTeam from './MyTeam';
import ProfileDesktopNav from './ProfileDesktopNav';
import ProfileMobileMenu from './ProfileMobileMenu';
import ProfileMobileSubpageHeader from './ProfileMobileSubpageHeader';
import ProfileMyTeamUpgradePanel from './ProfileMyTeamUpgradePanel';
import ProfileTabLoadingState from './ProfileTabLoadingState';
import UserDetails from './UserDetails';
import { findProfileSection, getProfileSections } from './profilePageSections';

const PROFILE_PLAN_SYNC_POLL_MS = 3000;
/** Poll at t=0,3,…,30s (11 samples over the first 30 seconds). */
const PROFILE_PLAN_SYNC_POLL_ROUNDS = 11;

export default function ProfilePage() {
  const { tab: sectionSlug } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { authStatus } = useSnapshot(authStore.state);
  const {
    loading: userLoading,
    organizationInfo,
    userInfo,
  } = useSnapshot(userStore.state);
  const teamInviteAcceptStarted = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle success message from payment
    if (params.has('success') && params.get('success') === 'true') {
      const newPlan = params.get('newPlan');
      const numberPurchasedSpins = params.get('numberPurchasedSpins');
      if (newPlan) {
        noticesStore.success('Thank you for signing up for Cartwheel!');
      } else if (numberPurchasedSpins) {
        const numberPurchasedSpinsInt = parseInt(numberPurchasedSpins);
        noticesStore.success(
          `You have just purchased ${numberPurchasedSpinsInt} more spins! They may take a few minutes to activate.`,
        );

        // In 5 seconds, kick off a new fetch userInfo call
        // and hopefully the new spins will be there.
        setTimeout(() => {
          userStore.load(false);
        }, 5000);
      }

      // Clean up URL by removing all query parameters
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.has('canceled') && params.get('canceled') === 'true') {
      noticesStore.info(
        "Your purchase was canceled, if there's anything we can do better, please let us know!",
      );
      // Clean up URL by removing all query parameters
      window.history.replaceState(null, '', window.location.pathname);
    }

    // This param allows us to check (and redirect) from stripe email links
    if (params.has('manageSubscription') && userInfo.stripeCustomerID) {
      userStore.manageSubscription(userInfo.stripeCustomerID);
    }
  }, [userInfo]);

  useEffect(() => {
    if (authStatus !== 'user') return undefined;

    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const syncPlanData = () => {
      void (async () => {
        await userStore.refetchUserInfo({ assignOnlyIfChanged: true });
        const stripeId = userStore.state.userInfo.stripeCustomerID;
        if (stripeId) {
          await customerStore.fetchCustomer(stripeId, {
            force: true,
            silent: true,
          });
        }
      })();
    };

    for (let i = 0; i < PROFILE_PLAN_SYNC_POLL_ROUNDS; i += 1) {
      timeouts.push(setTimeout(syncPlanData, i * PROFILE_PLAN_SYNC_POLL_MS));
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [authStatus]);

  useEffect(() => {
    if (authStatus !== 'user' || teamInviteAcceptStarted.current) return;
    const invite = readPendingTeamInviteFromSearch();
    if (!invite) return;
    teamInviteAcceptStarted.current = true;
    void (async () => {
      const ok = await userStore.acceptPendingOrganizationInvite(
        invite.organizationID,
        invite.teamInviteId,
      );
      if (ok) {
        noticesStore.success('You have joined the team.');
        window.history.replaceState(null, '', window.location.pathname);
      } else {
        teamInviteAcceptStarted.current = false;
      }
    })();
  }, [authStatus]);

  const inviteInUrl = !!readPendingTeamInviteFromSearch();

  const missingOrgForMyTeam =
    (userInfo.email && !organizationInfo) ||
    organizationInfo?.organizationID === '';

  const hasMyTeamAccess = !missingOrgForMyTeam || inviteInUrl;

  const orgPlanType = organizationInfo?.planType;
  const isIndividualWorkspacePlan =
    orgPlanType === 'individual_basic' || orgPlanType === 'individual_pro';

  const myTeamContent = orgPlanType ? (
    isLegacyOrganizationPlan(orgPlanType) ? (
      <MyTeam />
    ) : isIndividualWorkspacePlan ? (
      <div className="overview max-w-(--breakpoint-md) mx-auto">
        <div className="mt-8 flex flex-col items-center">
          <Link
            to="/pricing/team-plans"
            className="inline-flex"
            style={{ textDecoration: 'none' }}
          >
            <Button variant="primary" size="lg">
              Upgrade to a team plan
            </Button>
          </Link>
        </div>
      </div>
    ) : (
      <MyNewTeam />
    )
  ) : inviteInUrl ? (
    <Text variant="web/body/md" className="text-gray-45">
      {authStatus === 'user'
        ? 'Completing your team invite…'
        : 'Sign in to accept your team invitation.'}
    </Text>
  ) : null;

  const resolvedMyTeamContent = hasMyTeamAccess ? (
    myTeamContent
  ) : userLoading ? (
    <ProfileTabLoadingState maxWidthClassName="max-w-(--breakpoint-md)" />
  ) : (
    <ProfileMyTeamUpgradePanel />
  );

  const myTeamMaxWidthClassName =
    hasMyTeamAccess && orgPlanType
      ? isLegacyOrganizationPlan(orgPlanType) || isIndividualWorkspacePlan
        ? 'max-w-(--breakpoint-md)'
        : 'max-w-(--breakpoint-lg)'
      : 'max-w-(--breakpoint-md)';

  const sections = getProfileSections({
    myTeamContent: resolvedMyTeamContent,
    myTeamMaxWidthClassName,
  });
  const activeSection = findProfileSection(sections, sectionSlug);
  const desktopSection = activeSection ?? sections[0];
  const sectionRedirectPath = isDesktop ? '/profile/overview' : '/profile';
  const pageClassName = 'mx-6 mb-20 lg:container';
  const mobileContentClassName = 'mx-auto max-w-(--breakpoint-md)';
  const desktopUserDetailsClassName = `mx-auto ${
    desktopSection.maxWidthClassName ?? 'max-w-(--breakpoint-md)'
  }`;

  if (sectionSlug && !activeSection) {
    return <Navigate to={sectionRedirectPath} replace />;
  }

  if (!isDesktop) {
    if (!sectionSlug) {
      return (
        <main className="mt-8">
          <div className={pageClassName}>
            <div className={mobileContentClassName}>
              <UserDetails userInfo={userInfo} loading={userLoading} />
            </div>
            <ProfileMobileMenu
              sections={sections}
              className={`${mobileContentClassName} mt-8`}
              onSelect={(nextSectionSlug) =>
                navigate(`/profile/${nextSectionSlug}`)
              }
            />
          </div>
        </main>
      );
    }

    const mobileSection = activeSection;

    return (
      <main className="mt-8">
        <div className={pageClassName}>
          <div className={mobileContentClassName}>
            <ProfileMobileSubpageHeader
              title={mobileSection?.mobileTitle ?? mobileSection?.label ?? ''}
              className="mb-6"
              onBack={() => navigate('/profile')}
            />
            {mobileSection?.content}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="mt-8">
      <div className={pageClassName}>
        <div className="hidden gap-4 md:flex">
          <ProfileDesktopNav
            sections={sections}
            activeSlug={desktopSection.slug}
            onSelect={(nextSectionSlug) =>
              navigate(`/profile/${nextSectionSlug}`)
            }
          />
          <div className="min-w-0 flex-auto">
            <div className={desktopUserDetailsClassName}>
              <UserDetails userInfo={userInfo} loading={userLoading} />
            </div>
            {desktopSection.content}
          </div>
        </div>
      </div>
    </main>
  );
}

// Utils: --------------------------------------------------------------------
function readPendingTeamInviteFromSearch(): {
  organizationID: string;
  teamInviteId: string;
} | null {
  const params = new URLSearchParams(window.location.search);
  const organizationID = params.get('organizationID');
  const teamInviteId = params.get('teamInviteId');
  if (!organizationID || !teamInviteId) return null;
  return { organizationID, teamInviteId };
}
